import { useState, useEffect, useRef } from 'react';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import {
  subscribeManyRounds,
  subscribeManyRoundsRacers,
  subscribeManyRoundsPicks,
  subscribeRoundsPicks,
  getManyRounds,
  RoundsResponse,
  RoundsRacersResponse,
  RoundsPicksResponse,
} from '@/lib/collections/rounds';
import {
  LOTTERY_DURATION_SEC,
  REVEAL_DURATION_SEC,
  CHOOSING_CUTOFF_SEC,
} from '@/lib/constants';

const LOTTERY_DURATION = parseInt(LOTTERY_DURATION_SEC, 10);   // 30s
const REVEAL_DURATION = parseInt(REVEAL_DURATION_SEC, 10);     // 30s
const CHOOSING_CUTOFF = parseInt(CHOOSING_CUTOFF_SEC, 10);     // 300s before race end

export type RacePhase = 'lottery' | 'reveal' | 'racing' | 'finished' | 'waiting';

export interface CurrentRoundState {
  round: RoundsResponse | null;
  racers: RoundsRacersResponse[];
  picks: RoundsPicksResponse[];
  userPick: RoundsPicksResponse | null;
  secondsLeft: number;
  phase: RacePhase;
  /** True when the choosing cutoff has not yet passed (user can still pick). */
  choosingOpen: boolean;
  /** Seconds until the next round starts (useful during finished phase). */
  secondsUntilNextRound: number;
  loading: boolean;
}

/**
 * Derives the current race phase from a round's stored timestamps and nowSec.
 *
 * Timeline (relative to lotteryStartTime):
 *   0s  – LOTTERY_DURATION  : lottery  (coins selected, no base prices yet)
 *   LOTTERY_DURATION – raceStartTime : reveal (show chosen coins)
 *   raceStartTime – raceEndTime      : racing (live price battle)
 *   raceEndTime+                     : finished (result shown; next round creates immediately)
 *
 * The raceStartTime / raceEndTime stored on the round document are the
 * authoritative source of truth — no UTC boundary math needed.
 */
function derivePhase(round: RoundsResponse, nowSec: number): RacePhase {
  const revealStart = round.lotteryStartTime + LOTTERY_DURATION;
  const raceStart = round.raceStartTime;
  const raceEnd = round.raceEndTime;

  if (nowSec < revealStart) return 'lottery';
  if (nowSec < raceStart) return 'reveal';
  if (nowSec < raceEnd) return 'racing';
  // raceEnd has passed — finished (the backend will create the next round and it
  // will appear via the "latest round" subscription within the next tick)
  return 'finished';
}

export function useCurrentRound(userAddress?: string): CurrentRoundState {
  // Client-side clock tick — updates every second
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const interval = setInterval(() => {
      setNowSec(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Polled fallback round — used when the realtime subscription hasn't delivered
  // the latest doc yet (e.g., a new round just started and push hasn't fired).
  const [polledRound, setPolledRound] = useState<RoundsResponse | null>(null);
  const retryRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Subscribe to the most recent round by lotteryStartTime — this always gives
  // us the latest round regardless of its ID, which changes each round.
  const { data: roundsLatest, loading: roundsLatestLoading } = useRealtimeData<RoundsResponse[]>(
    subscribeManyRounds,
    true,
    'order by lotteryStartTime desc limit 1'
  );

  const subscriptionRound = roundsLatest?.[0] ?? null;

  // When the subscription delivers a round with a different roundId than polledRound,
  // clear polledRound so the subscription is always the source of truth.
  useEffect(() => {
    if (polledRound && subscriptionRound && subscriptionRound.roundId !== polledRound.roundId) {
      setPolledRound(null);
    }
  }, [subscriptionRound?.roundId, polledRound?.roundId]); // eslint-disable-line react-hooks/exhaustive-deps

  const latestRound = subscriptionRound ?? polledRound ?? null;

  // If latestRound.status is 'finished' AND the clock says the race is over,
  // we hold the finished round in view until the backend creates the next one.
  // When the next round appears (status: 'lottery'), the subscription will
  // deliver it and latestRound will update naturally — no flicker.
  const round = latestRound;

  // Derive phase from the round's stored timestamps, not from UTC anchoring
  const phase: RacePhase = round ? derivePhase(round, nowSec) : 'waiting';

  // When the derived phase says 'finished' but the DB status is still 'racing'
  // (the heartbeat hasn't settled yet), trust the clock — show finished.
  // When the DB says 'finished' or 'results', always show finished regardless of clock.
  // ('results' is the 60s post-race window before the next round is created.)
  const effectivePhase: RacePhase =
    !round
      ? 'waiting'
      : round.status === 'finished' || round.status === 'results'
        ? 'finished'
        : phase;

  // Compute countdown
  let secondsLeft = 0;
  let secondsUntilNextRound = 0;

  if (round) {
    const revealStart = round.lotteryStartTime + LOTTERY_DURATION;
    const raceStart = round.raceStartTime;
    const raceEnd = round.raceEndTime;

    if (effectivePhase === 'lottery') {
      secondsLeft = Math.max(0, revealStart - nowSec);
    } else if (effectivePhase === 'reveal') {
      secondsLeft = Math.max(0, raceStart - nowSec);
    } else if (effectivePhase === 'racing') {
      secondsLeft = Math.max(0, raceEnd - nowSec);
    } else if (effectivePhase === 'finished') {
      // Show countdown to when the next round will be created
      // (raceEndTime + 50s is when the results window expires and the next round starts).
      const nextRoundTime = round.raceEndTime + 50;
      secondsUntilNextRound = Math.max(0, nextRoundTime - nowSec);
      secondsLeft = secondsUntilNextRound;
    }
  }

  // Picking is only open once the race starts and before the cutoff.
  const choosingOpen = round
    ? nowSec >= round.raceStartTime && nowSec < round.raceEndTime - CHOOSING_CUTOFF
    : false;

  // Active retry: when the subscription hasn't delivered a new round yet,
  // poll every 3s imperatively to catch new documents faster.
  const isWaiting = !round;
  // Poll during results/finished phases so we catch the new round shortly after
  // the backend creates it (within ~3s of the 60s results window expiring).
  const isFinishedWaitingForNext =
    (round?.status === 'results' || round?.status === 'finished') &&
    effectivePhase === 'finished';

  // Stable ref to the current round so the interval callback can read it without
  // being listed as a dependency (avoids tearing down / re-arming on status changes).
  const roundRef = useRef(round);
  roundRef.current = round;

  useEffect(() => {
    const shouldPoll = isWaiting || isFinishedWaitingForNext;

    if (!shouldPoll) {
      if (retryRef.current) {
        clearInterval(retryRef.current);
        retryRef.current = null;
      }
      return;
    }

    // Already polling — don't create a second interval.
    if (retryRef.current) return;

    const doFetch = async () => {
      try {
        const results = await getManyRounds('order by lotteryStartTime desc limit 1');
        const fetched = results?.[0];
        if (!fetched) return;

        const currentRound = roundRef.current;
        const existingId = currentRound?.roundId;

        if (fetched.roundId !== existingId) {
          // A genuinely new round appeared — update polledRound and stop polling;
          // the subscription will take over once it catches up.
          setPolledRound(fetched);
          if (retryRef.current) {
            clearInterval(retryRef.current);
            retryRef.current = null;
          }
        } else if (fetched.status !== currentRound?.status) {
          // Same round, status changed (e.g. racing → results) — update polledRound.
          setPolledRound(fetched);
        }
      } catch {
        // silently ignore — next interval will retry
      }
    };

    doFetch();
    retryRef.current = setInterval(doFetch, 3000);

    return () => {
      if (retryRef.current) {
        clearInterval(retryRef.current);
        retryRef.current = null;
      }
    };
  // Deliberately exclude round?.status — we use roundRef to read it inside the
  // callback so the interval is NOT torn down on every status update.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWaiting, isFinishedWaitingForNext, round?.roundId]);

  const roundId = round?.roundId ?? '';

  const { data: racers } = useRealtimeData<RoundsRacersResponse[]>(
    subscribeManyRoundsRacers,
    !!roundId,
    roundId
  );

  const { data: picks } = useRealtimeData<RoundsPicksResponse[]>(
    subscribeManyRoundsPicks,
    !!roundId,
    roundId
  );

  const { data: userPick } = useRealtimeData<RoundsPicksResponse | null>(
    subscribeRoundsPicks,
    !!(roundId && userAddress),
    roundId,
    userAddress ?? ''
  );

  return {
    round,
    racers: racers ?? [],
    picks: picks ?? [],
    userPick: userPick ?? null,
    secondsLeft,
    phase: effectivePhase,
    choosingOpen,
    secondsUntilNextRound,
    loading: roundsLatestLoading && !polledRound,
  };
}
